import { gEnums } from '../../enums/globalEnums'
import { currentHelpers } from '../../redirection/current'
import { logPatches } from './reducerHelpers/logging'
import { produceWithPatches } from "immer";

const optionKeys = {
  appAuction: 'appAuction',
  appConversations: 'appConversations',
  appCredentialing: 'appCredentialing',
  appGallery: 'appGallery',
  appGamification: 'appGamification',
  appMeetings: 'appMeetings',
  appNetworking: 'appNetworking',
  appNotes: 'appNotes',
  appNotifications: 'appNotifications',
  appLocationTracking: 'appLocationTracking',
  appRating: 'appRating',
  appSurvey: 'appSurvey',
  appTicketing: 'appTicketing',
  appVideoConferencing: 'appVideoConferencing',
  appQuestionsAndAnswers: 'appQuestionsAndAnswers',
  appVoting: 'appVoting',
}

const rts = {
  handleActiveAll: 'handleActiveAll',
  handleActiveMenu: 'handleActiveMenu',
  handleActiveMenuGroup: 'handleActiveMenuGroup',
  handleAllowGroupMenuSort: 'handleAllowGroupMenuSort',
  handleMenuGroupItemClick: 'handleMenuGroupItemClick',
  handleShowSearch: 'handleShowSearch',
  handleUpdateAll: 'handleUpdateAll',
  handleUpdateInit: 'handleUpdateInit',
  handleShowQuickSearch: 'handleShowQuickSearch',
}

export const settingsMenuReducer = (state, action) => {

  const { lsName } = state
  const { type } = action

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    draft => {
      switch (type) {

        case rts.handleUpdateInit: {
          draft.isGlobal = action.isGlobal;
          draft.appArea = action.appArea;
          break;
        }

        case rts.handleMenuGroupItemClick: {
          draft.selectedGroupItem = action.selectedGroupItem;
          draft.selectedGroup = action.selectedGroup;
          break;
        }

        case rts.handleActiveAll:
        case rts.handleActiveMenu:
        case rts.handleShowSearch: {
          // no state changes
          break;
        }

        case rts.handleActiveMenuGroup: {
          const ams = draft.activeMenus;
          ams[action.name] = !ams[action.name];

          currentHelpers.storageItem_set(lsName, JSON.stringify(ams));

          draft.activeAll = !draft.activeAll;
          draft.activeMenu = action.name === draft.activeMenu ? null : action.name;
          break;
        }

        case rts.handleUpdateAll: {
          const activeMenus = draft.activeMenus;
          Object.keys(activeMenus).forEach(key => {
            activeMenus[key] = !draft.activeAll;
          });

          currentHelpers.storageItem_set(lsName, JSON.stringify(activeMenus));

          draft.activeAll = !draft.activeAll;
          break;
        }

        case rts.handleAllowGroupMenuSort: {
          draft.allowGroupMenuSort = !draft.allowGroupMenuSort;
          break;
        }

        case rts.handleShowQuickSearch: {
          draft.showQuickSearch = !draft.showQuickSearch;
          break;
        }

        default:
        // nothing
      }
    }
  );

  // 🔎 optional patch logging hook
  logPatches('settingsMenu', type, patches, action);

  return nextState;

}

export const settingsMenuInitialState = (init_state) => {
  return { ...init_state }
};

export const settingsMenuHandlers = (dispatch, state) => {
  return {
    handleActiveAll: () => { dispatch({ type: rts.handleActiveAll }) },
    handleActiveMenu: () => { dispatch({ type: rts.handleActiveMenu }) },
    handleActiveMenuGroup: (name) => { dispatch({ type: rts.handleActiveMenuGroup, name }) },
    handleMenuGroupItemClick: (selectedGroupItem, selectedGroup) => { dispatch({ type: rts.handleMenuGroupItemClick, selectedGroupItem, selectedGroup }) },
    handleShowSearch: () => { dispatch({ type: rts.handleShowSearch }) },
    handleUpdateAll: () => { dispatch({ type: rts.handleUpdateAll }) },
    handleUpdateInit: (appArea, isGlobal) => { dispatch({ type: rts.handleUpdateInit, appArea, isGlobal }) },
    handleAllowGroupMenuSort: () => { dispatch({ type: rts.handleAllowGroupMenuSort }) },
    handleShowQuickSearch: () => { dispatch({ type: rts.handleShowQuickSearch }) },
  }
}

export const settingsMenu_fns = (projectOptions, pageOptions, projectModules, groupItemClick_fns, pathViews, nonLandingView, settingsOriginType, productionSettings) => {
  return {
    allowProjectOption: (key) => allowProjectOption(key, pageOptions, projectOptions),
    allowProjectModule: (key) => allowProjectModule(key, projectModules),
    handleGroupItemClick: (selectedGroupItem) => handleGroupItemClick(selectedGroupItem, { groupItemClick_fns, pathViews, nonLandingView, settingsOriginType, productionSettings }),
  }
}

// exported function
const allowProjectModule = (key, projectModules) => {

  const {
    allowAuction,
    allowCredentialing,
    allowGallery,
    allowGamification,
    allowLocationTracking,
    allowMeetings,
    allowNetworking,
    allowNotifications,
    allowQuestionsAndAnswers,
    allowRating,
    allowSurvey,
    allowTicketing,
    allowVideoConferencing,
    allowVoting,
  } = projectModules ?? {}

  switch (key) {
    case optionKeys.appAuction:
      return allowAuction
    case optionKeys.appCredentialing:
      return allowCredentialing
    case optionKeys.appGallery:
      return allowGallery
    case optionKeys.appGamification:
      return allowGamification
    case optionKeys.appLocationTracking:
      return allowLocationTracking
    case optionKeys.appMeetings:
      return allowMeetings
    case optionKeys.appNetworking:
      return allowNetworking
    case optionKeys.appNotifications:
    case optionKeys.notificationsManager:
      return allowNotifications
    case optionKeys.appQuestionsAndAnswers:
      return allowQuestionsAndAnswers
    case optionKeys.appRating:
      return allowRating
    case optionKeys.appSurvey:
      return allowSurvey
    case optionKeys.appTicketing:
      return allowTicketing
    case optionKeys.appVideoConferencing:
      return allowVideoConferencing
    case optionKeys.appVoting:
      return allowVoting
    default:
      return true
  }
}

// exported function
const allowProjectOption = (key, pageOptions, projectOptions) => {

  const {
    allowProfiles,
    allowFeeds,
    allowGeoList,
    allowGeoLocation,
    allowImageLinks,
    allowQuestionsAndAnswers,
    allowTickets,
    allowTwitter,
    allowVideoLinks,
    allowVoting,
  } = pageOptions ?? {}

  const _apits = gEnums.additionalPageItemTypes

  switch (key) {
    case _apits.feeds:
      return allowFeeds
    case _apits.feeds:
      return allowFeeds
    case _apits.geoList:
      return allowGeoList
    case _apits.geoLocation:
      return allowGeoLocation
    case _apits.imageLinks:
      return allowImageLinks
    case _apits.questionsAndAnswers:
      return allowQuestionsAndAnswers
    case _apits.tickets:
      return allowTickets
    case _apits.twitter:
      return allowTwitter
    case _apits.videoLinks:
      return allowVideoLinks
    case _apits.voting:
      return allowVoting
    default:
      return true
  }
}

// exported function
/**
 * This handles the click from the settings menu
 * @param {object} selectedGroupItem 
 * @param {object} gi 
 */
const handleGroupItemClick = (selectedGroupItem, props) => {

  const { groupItemClick_fns, pathViews, nonLandingView, settingsOriginType, productionSettings } = props

  // console.log('gi', gi)

  const {
    handleGroupItem,
    handleShow_appDashboard,
    handleShowSearch,
    navigation_fns,
    swipeToSelected,
  } = groupItemClick_fns

  const { settingsMenuGroupType, close, gotoKey, key, triggerUrl } = selectedGroupItem ?? {}

  switch (settingsMenuGroupType) {
    case gEnums.settingsMenuGroupTypes.appDashboard:
    case gEnums.settingsMenuGroupTypes.eventDashboard:
      handleShow_appDashboard(selectedGroupItem.key)
      break;

    default:
      if (triggerUrl) {
        console.log('MOVED')
        // openSettingsItem(triggerUrl, pathViews, nonLandingView, productionSettings)
      } else if (close) {
        console.log('close')
      } else if (gotoKey) {
        switch (gotoKey) {
          case 'search':
            handleShowSearch()
            break;
          default:
            navigation_fns.handleNav_item({ key: gotoKey })
            break;
        }
      } else {
        switch (settingsOriginType) {
          case gEnums.settingsOriginTypes.app:
          case gEnums.settingsOriginTypes.page:
            if (swipeToSelected) {
              const swipeProps = {
                swipedItem: {
                  key: key,
                  settingKey: key,
                }
              }
              swipeToSelected(swipeProps)
            }
            break;
          default:
            handleGroupItem(selectedGroupItem)
        }
      }
  }
}